import { useState } from 'react';
import {
  useCreateCallbackMutation,
  useDeleteCallbackMutation,
  useUpdateCallbackMutation,
} from '../store/modules/merchant/api';
import { CallbackResponse, ConfigResponse } from '../store/modules/merchant/types';
import { toast } from '../util/toast';
import { useConfigUpsert } from './useConfigUpsert';

export type FulfillmentState = {
  isShippingEnabled: boolean;
  isOrderResolveEnabled: boolean;
  isOrderChangeEnabled: boolean;
  isOrderVerifyEnabled: boolean;
  callbackUrl: string;
};

type Props = {
  merchantId: string;
  originalFulfillmentState: FulfillmentState;
  noShippingRequiredConfig?: ConfigResponse;
  callbacks?: Array<CallbackResponse>;
};

export const useFullfillmentSubmit = ({
  merchantId,
  noShippingRequiredConfig,
  callbacks,
  originalFulfillmentState,
}: Props) => {
  const [createCallback] = useCreateCallbackMutation();
  const [deleteCallback] = useDeleteCallbackMutation();
  const [updateCallback] = useUpdateCallbackMutation();
  const [isLoading, setIsLoading] = useState(false);
  const { onConfigUpsert } = useConfigUpsert(merchantId);

  const submit = async (fulfillmentState: FulfillmentState) => {
    const original = originalFulfillmentState;
    const updated = fulfillmentState;

    // First handle the shipping config
    if (original.isShippingEnabled !== updated.isShippingEnabled) {
      await onConfigUpsert({
        ...noShippingRequiredConfig,
        name: 'NO_SHIPPING_REQUIRED',
        value: !updated.isShippingEnabled,
        enabled: true,
      });
    }

    // We should block updates to callbacks that are inherited from the parent merchant.
    // See the useCallbackUpsert hook for more details.
    const isAnyCallbackInherited = callbacks?.some((c) => c.isInherited);
    if (isAnyCallbackInherited) {
      throw new Error('The callback is inherited from the parent merchant and cannot be updated.');
    }

    const callbackPromises: Promise<unknown>[] = [];
    const callbackUpdates: {
      callbackType: CallbackResponse['callbackType'];
      originalCallback?: CallbackResponse;
      isEnabled: boolean;
    }[] = [
      {
        callbackType: 'Products',
        originalCallback: callbacks?.find((c) => c.callbackType === 'Products'),
        isEnabled: updated.isOrderResolveEnabled,
      },
      {
        callbackType: 'ShippingAndTax',
        originalCallback: callbacks?.find((c) => c.callbackType === 'ShippingAndTax'),
        isEnabled: updated.isOrderChangeEnabled,
      },
      {
        callbackType: 'Fulfillability',
        originalCallback: callbacks?.find((c) => c.callbackType === 'Fulfillability'),
        isEnabled: updated.isOrderVerifyEnabled,
      },
    ];

    const mustUpdateUrl = original.callbackUrl !== updated.callbackUrl;
    callbackUpdates.forEach((update) => {
      const { callbackType, originalCallback, isEnabled } = update;
      // If the callback doesn't exist and we want to disable it, do nothing
      if (!originalCallback && !isEnabled) return;

      // If the callback doesn't exist and we want to enable it, create it
      if (!originalCallback) {
        callbackPromises.push(
          createCallback({
            merchantId,
            callbackType,
            url: updated.callbackUrl,
          }).unwrap(),
        );
        return;
      }

      // If the callback exists and we want to disable it, delete it
      if (!isEnabled) {
        callbackPromises.push(
          deleteCallback({ callbackId: originalCallback.callbackId, merchantId }).unwrap(),
        );
        return;
      }

      // If the callback is enabled and the URL has changed, update it
      if (mustUpdateUrl) {
        callbackPromises.push(
          updateCallback({
            callbackId: originalCallback.callbackId,
            url: updated.callbackUrl,
            merchantId,
          }).unwrap(),
        );
      }
    });

    await Promise.all(callbackPromises);
  };

  const submitFullfillment = async (fulfillmentState: FulfillmentState) => {
    setIsLoading(true);
    try {
      await submit(fulfillmentState);
      toast.success('Fulfillment settings saved successfully.');
    } catch (err) {
      toast.error('Something went wrong. Please try again later.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    submitFullfillment,
    isLoading,
  };
};
